/**
 * The dialog an advisor agrees in before a CRM is connected.
 *
 * It only renders: what is shown, whether the box is ticked, and what happens on agreeing all come
 * from `useCrmConnectionConsentGate`, which the Connect button owns. Nothing is connected until the
 * box is ticked and the consent has been saved.
 */
import { useId } from 'react';
import { AlertCircle } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@design-system';
import type { useCrmConnectionConsentGate } from '@client/hooks/useCrmConnectionConsentGate';

interface CrmConnectionConsentDialogProps {
  crmName: string;
  gate: ReturnType<typeof useCrmConnectionConsentGate>;
}

export function CrmConnectionConsentDialog({ crmName, gate }: CrmConnectionConsentDialogProps) {
  const acknowledgeId = useId();

  return (
    <Dialog
      open={gate.isOpen}
      onOpenChange={open => {
        if (!open) gate.cancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Before you connect {crmName}</DialogTitle>
          <DialogDescription className="text-gray-800">{gate.text}</DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-3">
          <Checkbox
            id={acknowledgeId}
            checked={gate.isAcknowledged}
            disabled={gate.isSaving}
            onCheckedChange={checked => gate.setIsAcknowledged(checked === true)}
          />
          <Label
            htmlFor={acknowledgeId}
            className="leading-normal font-normal text-gray-900"
          >
            {gate.acknowledgement}
          </Label>
        </div>

        {!!gate.error && (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{(gate.error as Error).message}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={gate.cancel}
            disabled={gate.isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={gate.agree}
            disabled={!gate.isAcknowledged || gate.isSaving}
          >
            {gate.isSaving ? 'Saving...' : 'Agree and connect'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
